/**
 * The employee page's top and its Oversikt tab (owner brief and mockup, 2026-09-21): four figures
 * that answer "is this person at work, when next, how is the week, what is waiting", then six
 * cards. Every card links to the tab that holds the rest. Only settings that exist are shown
 * (owner, round 2: no placeholders for overtime, averaging, flexitime).
 */
import type { ApiAbsence, ApiShift, ApiUserFull } from '@/api/data';
import { T } from '@/i18n';
import { oslo, today, worked } from '@/rules';
import { breakCalcLabel } from '@/rules/breakSetting';
import { useTenant } from '@/shell/tenant';
import { Chip, Panel } from '@/ui';
import { DN, DNL, MONTHS, fmtD, fmtDs, fmtPhone, num, typeName } from '@/ui/format';
import { Icon, type IconName } from '@/ui/icons';
import type { BreakMode } from '@timefly/core';
import type { BreakPolicy } from '@timefly/core';
import { dow } from '@timefly/core';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { toVaktplan } from '../vaktplan/link';
import { EMPLOYMENT_FORMS, endNotice, ruleWeek, stillingsprosent } from './roster';
import { hm, requests, upcomingAbsence } from './summary';

/** Whole days from today to `d`, both Oslo dates. */
const daysUntil = (d: string) =>
  Math.round((Date.parse(`${d}T00:00:00Z`) - Date.parse(`${today()}T00:00:00Z`)) / 86_400_000);

const dateOf = (s: ApiShift) => oslo(s.start_time)?.date ?? '';
const clock = (iso: string | null | undefined) => oslo(iso)?.time?.slice(0, 5) ?? '';
/** "Man 22. september" */
const longDay = (d: string) =>
  `${DN[dow(d)]} ${Number(d.slice(8, 10))}. ${MONTHS[Number(d.slice(5, 7)) - 1] ?? ''}`;

function Figure({
  icon,
  label,
  value,
  sub,
  bar,
  to,
  linkText,
}: {
  icon: IconName;
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  bar?: number;
  to: string;
  linkText: string;
}) {
  return (
    <div className="hcard">
      <div className="hchead">
        <span className="hci act">
          <Icon name={icon} size={24} />
        </span>
        <span className="hctext">
          <span className="hcl">{label}</span>
          <b className="hcv sm">{value}</b>
          {sub}
        </span>
      </div>
      {bar != null && (
        <span className="hcx">
          <span className="hbar">
            <i style={{ width: `${bar}%` }} />
          </span>
          <small>{Math.round(bar)} %</small>
        </span>
      )}
      <Link className="hcgo" to={to}>
        {linkText} <span aria-hidden="true">→</span>
      </Link>
    </div>
  );
}

export function Figures({
  base,
  next,
  week,
  u,
  abs,
}: {
  base: string;
  next: ApiShift | null;
  week: { worked: number; planned: number; target: number | null; pct: number };
  u: ApiUserFull;
  abs: ApiAbsence[];
}) {
  const r = requests(abs);
  const left = u.vacation_days_balance;
  const per = u.total_vacation_days_per_year;
  const tasks = next?.taskAssignments?.length ?? 0;
  return (
    <div className="hcards afig">
      <Figure
        icon="calendar"
        label={T('Neste vakt')}
        value={next ? longDay(dateOf(next)) : T('Ingen planlagt')}
        sub={
          next ? (
            <small>
              {clock(next.start_time)}–{clock(next.end_time)}
              {next.shiftType?.name ? ` · ${next.shiftType.name}` : ''}
              {tasks ? ` · ${tasks} ${tasks === 1 ? T('oppgave') : T('oppgaver')}` : ''}
            </small>
          ) : undefined
        }
        to={`${base}/vakter`}
        linkText={T('Se vakter')}
      />
      <Figure
        icon="clock"
        label={T('Denne uken')}
        value={
          week.target ? (
            <>
              {num(week.worked)} t <span className="of">/ {num(week.target)} t</span>
            </>
          ) : (
            `${num(week.worked)} t`
          )
        }
        sub={
          <small>
            {week.target ? '' : `${T('avtalt tid mangler')} · `}
            {T('planlagt {n} t', { n: num(week.planned) })}
          </small>
        }
        {...(week.target ? { bar: week.pct } : {})}
        to={`${base}/tid`}
        linkText={T('Se tid og lønn')}
      />
      <Figure
        icon="sun"
        label={T('Ferie')}
        value={left == null ? '–' : `${num(left)} ${T('dager igjen')}`}
        sub={per ? <small>{T('{n} per år', { n: num(per) })}</small> : undefined}
        {...(left != null && per ? { bar: Math.max(0, Math.min(100, (left / per) * 100)) } : {})}
        to={`${base}/fravaer`}
        linkText={T('Se fravær')}
      />
      <Figure
        icon="mail"
        label={T('Søknader')}
        value={`${r.pending} ${T('venter')}`}
        sub={r.latest ? <small>{T('Siste søknad {d}', { d: fmtD(r.latest) })}</small> : undefined}
        to={`${base}/fravaer`}
        linkText={T('Se søknader')}
      />
    </div>
  );
}

const Title = ({ icon, children }: { icon: IconName; children: ReactNode }) => (
  <span className="ovt">
    <span className="ovi">
      <Icon name={icon} />
    </span>
    {children}
  </span>
);
const More = ({ to, children }: { to: string; children: ReactNode }) => (
  <Link className="ovmore" to={to}>
    {children}
  </Link>
);

/**
 * An agreed week nobody has set is "Avtalt tid mangler", never 0 and never an assumed 37,5: the rule
 * engine measures nothing against it (rules/index.ts toPerson). The link opens the field.
 */
export function AgreedMissing({
  u,
  onEdit,
}: { u: ApiUserFull; onEdit?: ((field?: string) => void) | undefined }) {
  // #54: say what the rules count with meanwhile, the company's week or 37,5 (agreedWeek.js)
  const { tenant } = useTenant();
  const week = ruleWeek(tenant?.agreed_week_hours != null ? Number(tenant.agreed_week_hours) : null);
  return (
    <>
      <Chip tone="warn" small>
        {u.max_hours_per_week == null
          ? T('Avtalt tid mangler')
          : T('Ugyldig: {n} t', { n: u.max_hours_per_week })}
      </Chip>
      {onEdit && (
        <>
          {' '}
          <button type="button" className="lnk" onClick={() => onEdit('agreed_week_hours')}>
            {T('Sett avtalt tid')}
          </button>
        </>
      )}
      <small className="ovwarn" data-testid="avtalt-mangler">
        {T('Avtalt arbeidstid per uke mangler. Reglene bruker {n} t til den er satt.', { n: num(week) })}
      </small>
    </>
  );
}

export function Oversikt({
  base,
  u,
  next,
  mine,
  abs,
  policy,
  breakMode,
  roleText,
  jobTitle,
  seeLoenn,
  seeSens,
  onEdit,
  files,
}: {
  base: string;
  u: ApiUserFull;
  next: ApiShift | null;
  mine: ApiShift[];
  abs: ApiAbsence[];
  policy: BreakPolicy | null;
  breakMode: BreakMode;
  roleText: string;
  /** the job title, or null when none is given; distinct from the access role */
  jobTitle?: string | null;
  seeLoenn: boolean;
  seeSens: boolean;
  /** opens Rediger ansatt, on a field when one is named */
  onEdit?: (field?: string) => void;
  files: ReactNode;
}) {
  const tasks = next?.taskAssignments ?? [];
  const done = tasks.filter((t) => t.status === 'completed').length;
  const recent = mine
    .filter((s) => s.punch_in_time)
    .sort((a, b) => b.start_time.localeCompare(a.start_time))
    .slice(0, 5);
  const ahead = upcomingAbsence(abs, today()).slice(0, 5);
  const teams = (u.teams ?? []).map((t) => t.name).filter(Boolean);
  // the contract (#108): stillingsprosent from the agreed weeks, and the end date against today
  const { tenant } = useTenant();
  const pct = stillingsprosent(
    u,
    tenant?.agreed_week_hours != null ? Number(tenant.agreed_week_hours) : 37.5,
  );
  const ending = endNotice(u, today());
  // the number the payroll file and the send carry: the Lønns-ID, else Ansattnr (#183: a Lønns-ID
  // overriding a right Ansattnr stopped a store's PowerOffice file on 2026-10-19)
  const payNumber = String(u.payroll_id ?? '').trim() || String(u.employee_id ?? '').trim();
  const poFits = tenant?.payroll_system !== 'poweroffice' || !payNumber || /^\d{1,6}$/.test(payNumber);
  return (
    <div className="ovgrid">
      <Panel item title={<Title icon="calendar">{T('Neste vakt')}</Title>}>
        {next ? (
          <>
            <div className="ovnext">
              <span>
                <b>{longDay(dateOf(next))}</b>
                <small>{next.shiftType?.name ?? T('Vakt')}</small>
              </span>
              <b className="mono">
                {clock(next.start_time)}–{clock(next.end_time)}
              </b>
            </div>
            {tasks.length > 0 && (
              <div className="ovtasks">
                <div className="ovtaskh">
                  {T('Oppgaver ({a} av {b} fullført)', { a: done, b: tasks.length })}
                  <span className="hbar">
                    <i style={{ width: `${(done / tasks.length) * 100}%` }} />
                  </span>
                </div>
                {tasks.map((t) => (
                  <div key={t.id} className={`ovtask${t.status === 'completed' ? ' done' : ''}`}>
                    <Icon name={t.status === 'completed' ? 'check' : 'clock'} />
                    {t.shiftTask?.name ?? T('Oppgave')}
                  </div>
                ))}
              </div>
            )}
            <More to={toVaktplan({ week: dateOf(next) })}>{T('Se i vaktplanen')} →</More>
          </>
        ) : (
          <p className="ovnone">{T('Ingen planlagte vakter de neste fire ukene.')}</p>
        )}
      </Panel>

      <Panel
        item
        title={<Title icon="user">{T('Person')}</Title>}
        {...(onEdit
          ? {
              actions: (
                <button type="button" className="btn sm" onClick={() => onEdit()}>
                  {T('Rediger')}
                </button>
              ),
            }
          : {})}
      >
        <dl className="ovdl">
          <dt>{T('Ansattnummer')}</dt>
          <dd>{u.employee_id || '–'}</dd>
          {u.payroll_id && (
            <>
              <dt>{T('Lønns-ID')}</dt>
              <dd>{u.payroll_id}</dd>
            </>
          )}
          <dt>{T('Til lønnssystemet')}</dt>
          <dd data-testid="pay-number">
            {payNumber || '–'}
            {u.payroll_id &&
              u.employee_id &&
              String(u.payroll_id).trim() !== String(u.employee_id).trim() && (
                <small style={{ display: 'block', color: 'var(--ink3)' }}>
                  {T('Lønns-ID brukes i stedet for ansattnummeret')}
                </small>
              )}
            {!poFits && (
              <small style={{ display: 'block', color: 'var(--dgr)' }}>
                {T('Passer ikke i PowerOffice: bare tall, høyst 6 siffer')}
              </small>
            )}
          </dd>
          {seeSens && (
            <>
              <dt>{T('Fødselsnummer')}</dt>
              <dd>{u.personal_number ? `•••••• ${String(u.personal_number).slice(-5)}` : '–'}</dd>
            </>
          )}
          <dt>{T('Stilling')}</dt>
          <dd>{jobTitle || <span className="ovmute">{T('Ikke oppgitt')}</span>}</dd>
          <dt>{T('Tilgangsrolle')}</dt>
          <dd>{roleText}</dd>
          <dt>{T('Team')}</dt>
          <dd>
            {teams.length ? (
              teams.map((t) => (
                <Chip key={t} tone="neu" small>
                  {t}
                </Chip>
              ))
            ) : (
              <span className="ovmute">{T('Ingen team')}</span>
            )}
          </dd>
          <dt>{T('Kontakt')}</dt>
          <dd className="ovcontact">
            {u.phone && (
              <a href={`tel:${u.phone.replace(/\s/g, '')}`}>
                <Icon name="phone" /> {fmtPhone(u.phone)}
              </a>
            )}
            {u.email ? (
              <a href={`mailto:${u.email}`}>
                <Icon name="mail" /> {u.email}
              </a>
            ) : (
              // added without an address (2026-09-29): no login, reset or mail until one is added
              <span className="ovmute" data-testid="no-email">
                <Icon name="mail" />
                {T('Ingen e-post ennå')}
                {onEdit && (
                  <>
                    {' · '}
                    <button type="button" className="lnk" onClick={() => onEdit('email')}>
                      {T('Legg inn e-post')}
                    </button>
                  </>
                )}
              </span>
            )}
            {u.address && (
              <span>
                <Icon name="pin" /> {u.address}
              </span>
            )}
          </dd>
        </dl>
      </Panel>

      <Panel
        item
        title={<Title icon="sun">{T('Kommende fravær')}</Title>}
        actions={<More to={`${base}/fravaer`}>{T('Se alt')}</More>}
        flush
      >
        {ahead.length ? (
          ahead.map((a) => {
            const from = oslo(a.start_date)?.date ?? '';
            const to = oslo(a.end_date)?.date ?? '';
            return (
              <div className="ovrow" key={a.id}>
                <span>
                  <b>{typeName(a.absence_type)}</b>
                  <small>
                    {fmtD(from)}
                    {to !== from ? ` – ${fmtD(to)}` : ''}
                  </small>
                </span>
                {a.status === 'pending' ? (
                  <Chip tone="neu" small>
                    {T('Venter')}
                  </Chip>
                ) : (
                  <Chip tone="ok" small>
                    {T('Innvilget')}
                  </Chip>
                )}
              </div>
            );
          })
        ) : (
          <p className="ovnone">{T('Ingen kommende fravær.')}</p>
        )}
      </Panel>

      <Panel
        item
        title={<Title icon="clock">{T('Siste registreringer')}</Title>}
        actions={<More to={`${base}/vakter`}>{T('Se alle')}</More>}
        flush
      >
        {recent.length ? (
          recent.map((s) => {
            const d = dateOf(s);
            return (
              <div className="ovrow" key={s.id}>
                <span className="ovday">
                  {DNL[dow(d)]?.slice(0, 3)} {fmtDs(d)}
                </span>
                <span className="mono">
                  {clock(s.punch_in_time)}–{s.punch_out_time ? clock(s.punch_out_time) : '?'}
                </span>
                <b className="mono">{hm(worked(s, policy))}</b>
              </div>
            );
          })
        ) : (
          <p className="ovnone">{T('Ingen stemplinger de siste fire ukene.')}</p>
        )}
      </Panel>

      <Panel
        item
        title={<Title icon="banknote">{T('Arbeidstid og lønn')}</Title>}
        actions={<More to={`${base}/tid`}>{T('Se detaljer')}</More>}
      >
        <dl className="ovdl">
          <dt>{T('Avtalt arbeidstid')}</dt>
          <dd>
            {u.max_hours_per_week != null && u.max_hours_per_week > 0 ? (
              `${num(u.max_hours_per_week)} t ${T('per uke')}${pct != null ? ` · ${pct} %` : ''}`
            ) : (
              <AgreedMissing u={u} onEdit={onEdit} />
            )}
          </dd>
          <dt>{T('Ansettelsesform')}</dt>
          <dd>
            {u.employment_form ? T(EMPLOYMENT_FORMS[u.employment_form]) : T('Ikke oppgitt')}
            {u.end_date && ` · ${T('til {d}', { d: fmtD(u.end_date) })}`}
            {ending && (
              <>
                {' '}
                <Chip tone={ending.kind === 'passed' ? 'dgr' : 'warn'} small>
                  {ending.kind === 'passed'
                    ? T('Sluttdatoen er passert')
                    : T('Slutter om {n} dager', { n: daysUntil(ending.date) })}
                </Chip>
              </>
            )}
          </dd>
          {seeLoenn && (
            <>
              <dt>{T('Lønnstype')}</dt>
              <dd>{u.contract_type === 'monthly' ? T('Fastlønn') : T('Timelønn')}</dd>
              <dt>{u.contract_type === 'monthly' ? T('Månedslønn') : T('Timelønn')}</dt>
              <dd>
                {u.contract_type === 'monthly' ? (
                  u.monthly_rate ? (
                    `${num(Number(u.monthly_rate))} kr`
                  ) : (
                    '–'
                  )
                ) : u.hourly_rate ? (
                  `${num(Number(u.hourly_rate))} kr`
                ) : (
                  <Chip tone="dgr" small>
                    {T('Mangler')}
                  </Chip>
                )}
              </dd>
            </>
          )}
          <dt>{T('Pauser')}</dt>
          <dd>{breakCalcLabel(breakMode)}</dd>
        </dl>
      </Panel>

      {files}
    </div>
  );
}
